import {
  Col,
  Form,
  Radio,
  Row,
  Select,
  Spin,
  Table,
  Tabs,
  Typography,
} from "antd";
import React, { Component } from "react";
import { PmsAPI } from "../../../controller/PmsAPI";
import moment from "moment";
import ResponsiveECharts from "../../components/ResponsiveECharts";
import DateWithWeekday from "../../components/DateWithWeekday";
import PmsRoomSelect from "../../components/PmsRoomSelect";

// 總房數如何被分掉，最後才是還能賣的量 —— 讓「為什麼只剩這麼少」一眼看得出來。
// 前五欄對應恆等式：總房數 = 已訂 + 在住 + 維修 + 線上銷售 + 現場可售
// 最後一欄「可賣總量」= 線上銷售 + 現場可售，是前面兩欄的和，非獨立來源。
const houseRemainColumns = [
  {
    title: "使用房種",
    dataIndex: "room_cod",
    key: "room_cod",
  },
  {
    title: "總房數",
    dataIndex: "total",
    key: "total",
    align: "right",
  },
  {
    title: "已訂",
    dataIndex: "order_qnt",
    key: "order_qnt",
    align: "right",
  },
  {
    title: "在住",
    dataIndex: "use_qnt",
    key: "use_qnt",
    align: "right",
  },
  {
    title: "維修",
    dataIndex: "ooo_qnt",
    key: "ooo_qnt",
    align: "right",
  },
  {
    title: "線上銷售",
    dataIndex: "block_qnt",
    key: "block_qnt",
    align: "right",
  },
  {
    // 這一欄是 RMINV_MN.LEFT_QNT，只有櫃台/直訂能立刻賣的部分，不含線上銷售
    title: "現場可售",
    dataIndex: "remain",
    key: "remain",
    align: "right",
    render: (value) => (
      <span style={{ color: value <= 0 ? "#ff4d4f" : "inherit" }}>{value}</span>
    ),
  },
  {
    // 現場可售 + 線上銷售 = 當日還能賣的總量。兩者是同一批未售房，差別只在通路：
    // 現場可售可以立刻賣，線上銷售由 OTA 賣或請訂房組釋出後轉現場。
    title: "可賣總量",
    key: "sellable_total",
    align: "right",
    render: (_, row) => {
      const total = (row.remain || 0) + (row.block_qnt || 0);
      return <b style={{ color: total <= 0 ? "#ff4d4f" : "inherit" }}>{total}</b>;
    },
  },
];

export class HouseRatePage extends Component {
  state = {
    loading: false,
    detailLoading: false,
    periodType: "day",
    selectedRoomCod: null,
    houseRateChartOption: null,
    detailDate: null,
    details: [],
    saleDetails: [],
    houseRemainDetails: [],
    overviewData: [],
    activeTabKey: "1",
    // 快取過濾選項
    roomCodFilters: [],
    useCodFilters: [],
    saleRoomCodFilters: [],
    saleUseCodFilters: [],
  };
  componentDidMount() {
    this.refresh();
  }
  refresh = () => {
    this.updateHouseRateChartOption();
  };
  updateHouseRateChartOption = async () => {
    let result = null;
    this.setState({ loading: true });
    await PmsAPI.houserate
      .stat(this.state.periodType, this.state.selectedRoomCod)
      .then((response) => {
        result = response.data;
        console.log(result);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
    const items = result.items.map((item) => {
      console.log("Processing item:", item); // Debug log
      return {
        ...item,
        rent_avg: Math.round(item.rent_amt / item.use_qnt),
        dat: item.dat,
      };
    });

    // Store overview data for the table
    this.setState({ overviewData: items });
    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          crossStyle: {
            color: "#999",
          },
        },
      },
      grid: {
        bottom: 110,
      },
      dataZoom: [
        {
          show: true,
          bottom: 20,
          type: "slider",
        },
      ],
      legend: {
        // data: ['住房銷售額', '餐飲銷售額', '平均房價', '住房間數']
        show: true,
      },
      xAxis: [
        {
          type: "time",
          // data: items.map((item) => item.dat),
          axisPointer: {
            type: "shadow",
          },
          axisLabel: {
            formatter: (value, index) => {
              let today = false;
              if (
                moment().format("YYYY-MM-DD") ===
                moment(value).format("YYYY-MM-DD")
              ) {
                today = true;
              }
              let style = "bold";
              let result = null;
              if (today) style = "today";
              if (this.state.periodType === "day")
                result = `{yy}\n{${style}|{MM}}\n{${style}|{dd}}\n{${style}|{ee}}`;
              else if (this.state.periodType === "month")
                result = `{yyyy}\n{${style}|{MM}}`;
              else result = `{yyyy}`;
              return result;
            },
            rich: {
              bold: {
                fontWeight: 700,
              },
              today: {
                color: "#aa5555",
                fontWeight: 700,
              },
            },
          },
        },
      ],
      dataset: {
        source: items,
      },
      yAxis: [
        {
          type: "value",
          name: "金額",
          min: 0,
          axisLabel: {
            formatter: "NT$ {value}",
            align: "right",            
          },
        },
        {
          type: "value",
          name: "間數",
          min: 0,
          offset: 30,
          axisLabel: {
            formatter: "{value}",
            align: "right",
          },
        },
        {
          type: "value",
          name: "百分比 (%)",
          min: 0,
          max: 100,
          offset: 80,
          axisLabel: {
            formatter: "{value}%",
            align: "right",
          },
        },
      ],
      series: [
        {
          name: "住房銷售額",
          type: "bar",
          stack: "total",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "rent_amt" },
          markLine: {
            silent: true,
            symbol: "none",
            lineStyle: {
              color: "#ff4d4f",
              type: "dashed",
              width: 2,
            },
            label: {
              show: true,
              position: "insideEndTop",
              formatter: "今日",
              color: "#ff4d4f",
              fontWeight: "bold",
            },
            data: [
              {
                xAxis: moment().format("YYYY-MM-DD"),
              },
            ],
          },
        },
        {
          name: "餐飲銷售額",
          type: "bar",
          stack: "total",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "rs_amt" },
        },
        {
          yAxisIndex: 1,
          name: "平均房價",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "rent_avg" },
        },
        {
          yAxisIndex: 0,
          name: "總收入",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "total_revenue" },
        },
        {
          yAxisIndex: 1,
          name: "ADR",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "adr" },
        },
        {
          yAxisIndex: 1,
          name: "RevPAR",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return "NT$ " + Number(value).toLocaleString();
            },
          },
          encode: { x: "dat", y: "revpar" },
        },
        {
          yAxisIndex: 1,
          name: "住房間數",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return value + " 間";
            },
          },
          encode: { x: "dat", y: "use_qnt" },
        },
        {
          yAxisIndex: 1,
          name: "總房間數",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return value + " 間";
            },
          },
          encode: { x: "dat", y: "total_rooms" },
        },
        {
          yAxisIndex: 2,
          name: "住房率",
          type: "line",
          symbol: "none",
          tooltip: {
            valueFormatter: function (value) {
              return Number(value).toFixed(2) + "%";
            },
          },
          encode: { x: "dat", y: "occupancy_rate" },
        },
      ],
    };
    this.setState({ houseRateChartOption: option });
  };
  handleClickChartEvent = (e) => {
    const date = e.data.dat;
    this.fetchDetail(date);
    // Switch to detail tab when chart is clicked
    this.setState({ activeTabKey: "2" });
    if (e.seriesIndex === 0) {
      this.setState({ filterType: "not-canceled" });
    } else {
      this.setState({ filterType: "canceled" });
    }
    console.log("click", e);
  };
  fetchDetail = (date) => {
    this.setState({ detailLoading: true, detailDate: date });
    const promise1 = PmsAPI.houserate
      .index(this.state.periodType, date, this.state.selectedRoomCod)
      .then((response) => {
        const result = response.data;
        console.log(result);
        this.setState({ details: result.items });
      })
      .finally(() => {
        // this.setState({ detailLoading: false });
      });
    const promise2 = PmsAPI.houserate
      .remain(this.state.periodType, date, this.state.selectedRoomCod)
      .then((response) => {
        const result = response.data;
        console.log(result);
        this.setState({ houseRemainDetails: result.items });
      })
      .finally(() => {
        // this.setState({ detailLoading: false });
      });

    const promise3 = PmsAPI.sale
      .index(this.state.periodType, date, this.state.selectedRoomCod)
      .then((response) => {
        const result = response.data;
        console.log(result);
        this.setState({ saleDetails: result.items });
      })
      .finally(() => {
        // this.setState({ detailLoading: false });
      });
    Promise.all([promise1, promise2, promise3])
      .then(() => {
        // 更新過濾選項快取
        this.updateFilterOptions();
      })
      .finally(() => {
        this.setState({ detailLoading: false });
      });
  };
  handleSelectDateType = (dateType) => {
    console.log("handleSelectDateType", dateType);
    this.setState({ dateType: dateType }, () => this.refresh());
  };
  handleSelectPeriodType = (value) => {
    console.log("handleSelectPeriodType", value);
    this.setState({ periodType: value }, () => this.refresh());
  };
  handleSelectRoomCode = (roomCod) => {
    console.log("handleSelectRoomCode", roomCod);
    this.setState({ selectedRoomCod: roomCod }, () => this.refresh());
  };
  handleSaleSelectFilter = (e) => {
    console.log(e.target.value);
    this.setState({ filterType: e.target.value });
  };
  getSaleDetails = () => {
    return this.state.saleDetails.filter((item) => {
      switch (this.state.filterType) {
        case "not-canceled":
          return item.cancel_dat === null;
        case "canceled":
          return item.cancel_dat !== null;
        case "all":
        default:
          return true;
      }
    });
  };

  // 獲取總覽表格欄位定義
  getOverviewColumns = () => {
    return [
      {
        title: "日期",
        dataIndex: "dat",
        key: "dat",
        sorter: (a, b) => new Date(a.dat).getTime() - new Date(b.dat).getTime(),
        render: (date) => <DateWithWeekday date={date} />,
      },
      {
        title: "住房間數",
        dataIndex: "use_qnt",
        key: "use_qnt",
        align: "right",
        sorter: (a, b) => a.use_qnt - b.use_qnt,
      },
      {
        title: "總房間數",
        dataIndex: "total_rooms",
        key: "total_rooms",
        align: "right",
        sorter: (a, b) => a.total_rooms - b.total_rooms,
      },
      {
        title: "住房率 (%)",
        dataIndex: "occupancy_rate",
        key: "occupancy_rate",
        align: "right",
        render: (value) => {
          const numValue = Number(value);
          let color = '';
          
          if (numValue < 30) {
            color = 'red';
          } else if (numValue > 60) {
            color = 'green';
          }
          
          return (
            <span style={{ color }}>
              {numValue.toFixed(2)}%
            </span>
          );
        },
        sorter: (a, b) => a.occupancy_rate - b.occupancy_rate,
      },
      {
        title: "房租收入",
        dataIndex: "rent_amt",
        key: "rent_amt",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.rent_amt - b.rent_amt,
      },
      {
        title: "餐飲收入",
        dataIndex: "rs_amt",
        key: "rs_amt",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.rs_amt - b.rs_amt,
      },
      {
        title: "總收入",
        dataIndex: "total_revenue",
        key: "total_revenue",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.total_revenue - b.total_revenue,
      },
      {
        title: "ADR",
        dataIndex: "adr",
        key: "adr",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.adr - b.adr,
      },
      {
        title: "RevPAR",
        dataIndex: "revpar",
        key: "revpar",
        align: "right",
        render: (value) => {
          const numValue = Number(value);
          let color = '';
          
          if (numValue < 1000) {
            color = 'red';
          } else if (numValue > 3000) {
            color = 'green';
          }
          
          return (
            <span style={{ color }}>
              {numValue.toLocaleString()}
            </span>
          );
        },
        sorter: (a, b) => a.revpar - b.revpar,
      },
      {
        title: "操作",
        key: "action",
        align: "center",
        fixed: "right",
        render: (value, record) => (
          <Typography.Link
            onClick={() => {
              this.fetchDetail(record.dat);
              this.setState({ activeTabKey: "2", detailDate: record.dat });
            }}
          >
            明細
          </Typography.Link>
        ),
      },
    ];
  };

  // 更新過濾選項快取
  updateFilterOptions = () => {
    const roomCodFilters = [
      ...new Set(
        this.state.details.map((item) => item.room_cod).filter(Boolean)
      ),
    ].map((value) => ({
      text: value,
      value: value,
    }));

    const useCodFilters = [
      ...new Set(
        this.state.details.map((item) => item.use_cod).filter(Boolean)
      ),
    ].map((value) => ({
      text: value,
      value: value,
    }));

    const saleRoomCodFilters = [
      ...new Set(
        this.state.saleDetails.map((item) => item.room_cod).filter(Boolean)
      ),
    ].map((value) => ({
      text: value,
      value: value,
    }));

    const saleUseCodFilters = [
      ...new Set(
        this.state.saleDetails.map((item) => item.use_cod).filter(Boolean)
      ),
    ].map((value) => ({
      text: value,
      value: value,
    }));

    this.setState({
      roomCodFilters,
      useCodFilters,
      saleRoomCodFilters,
      saleUseCodFilters,
    });
  };

  // 獲取動態 columns (使用快取)
  getColumns = () => {
    return [
      {
        title: "訂單卡號",
        dataIndex: "ikey",
        key: "ikey",
        sorter: (a, b) =>
          `${a.ikey}-${a.ikey_seq_nos}`?.localeCompare(
            `${b.ikey}-${b.ikey_seq_nos}`
          ),
      },
      {
        title: "項次",
        dataIndex: "ikey_seq_nos",
        key: "ikey_seq_nos",
        sorter: (a, b) =>
          `${a.ikey}-${a.ikey_seq_nos}`?.localeCompare(
            `${b.ikey}-${b.ikey_seq_nos}`
          ),
      },
      {
        title: "訂房來源",
        dataIndex: "cust_nam",
        key: "cust_nam",
        sorter: (a, b) => a.cust_nam?.localeCompare(b.cust_nam),
      },
      {
        title: "房價代碼",
        dataIndex: "rate_cod",
        key: "rate_cod",
        sorter: (a, b) => a.rate_cod?.localeCompare(b.rate_cod),
      },
      {
        title: "使用房種",
        dataIndex: "room_cod",
        key: "room_cod",
        sorter: (a, b) => a.room_cod?.localeCompare(b.room_cod),
        filters: this.state.roomCodFilters,
        onFilter: (value, record) => record.room_cod === value,
        filterSearch: true,
      },
      {
        title: "計價房種",
        dataIndex: "use_cod",
        key: "use_cod",
        sorter: (a, b) => a.use_cod?.localeCompare(b.use_cod),
        filters: this.state.useCodFilters,
        onFilter: (value, record) => record.use_cod === value,
        filterSearch: true,
      },
      {
        title: "住房間數",
        dataIndex: "use_qnt",
        key: "use_qnt",
        sorter: (a, b) => a.use_qnt - b.use_qnt,
      },
      {
        title: "到達",
        dataIndex: "todayci_qnt",
        key: "todayci_qnt",
        sorter: (a, b) => a.todayci_qnt - b.todayci_qnt,
      },
      {
        title: "離店",
        dataIndex: "todayco_qnt",
        key: "todayco_qnt",
        sorter: (a, b) => a.todayco_qnt - b.todayco_qnt,
      },
      {
        title: "續住",
        dataIndex: "continue_qnt",
        key: "continue_qnt",
        sorter: (a, b) => a.continue_qnt - b.continue_qnt,
      },
      {
        title: "訂房間數",
        dataIndex: "order_qnt",
        key: "order_qnt",
        sorter: (a, b) => a.order_qnt - b.order_qnt,
      },
      {
        title: "取消間數",
        dataIndex: "cancel_qnt",
        key: "cancel_qnt",
        sorter: (a, b) => a.cancel_qnt - b.cancel_qnt,
      },
      {
        title: "房租收入",
        dataIndex: "rent_amt",
        key: "rent_amt",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.rent_amt - b.rent_amt,
      },
      {
        title: "餐飲收入",
        dataIndex: "rs_amt",
        key: "rs_amt",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.rs_amt - b.rs_amt,
      },
      {
        title: "小計",
        dataIndex: "rent_amt",
        key: "total_amt",
        align: "right",
        render: (value, record) => {
          const total = record.rent_amt + record.rs_amt;
          return Number(total).toLocaleString();
        },
        sorter: (a, b) => a.rent_amt + a.rs_amt - (b.rent_amt + b.rs_amt),
      },
      {
        title: "平均房價",
        dataIndex: "rent_amt",
        key: "rent_avg",
        align: "right",
        fixed: "right",
        render: (value, record) => {
          if (!record.use_qnt) return "-";
          const total = record.rent_amt / record.use_qnt;
          if (total < (3200 - 600) * 0.7) {
            return (
              <span style={{ color: "red " }}>
                {Number(total).toLocaleString()}
              </span>
            );
          }
          return Number(total).toLocaleString();
        },
        sorter: (a, b) => {
          const aAvg = a.use_qnt ? a.rent_amt / a.use_qnt : 0;
          const bAvg = b.use_qnt ? b.rent_amt / b.use_qnt : 0;
          return aAvg - bAvg;
        },
      },
      {
        title: "平均房價 (含餐)",
        dataIndex: "rent_amt",
        key: "rent_avg_with_rs",
        align: "right",
        fixed: "right",
        render: (value, record) => {
          if (!record.use_qnt) return "-";
          const total = (record.rent_amt + record.rs_amt) / record.use_qnt;
          if (total < 3200 * 0.7) {
            return (
              <span style={{ color: "red " }}>
                {Number(total).toLocaleString()}
              </span>
            );
          }
          return Number(total).toLocaleString();
        },
        sorter: (a, b) => {
          const aAvg = a.use_qnt ? (a.rent_amt + a.rs_amt) / a.use_qnt : 0;
          const bAvg = b.use_qnt ? (b.rent_amt + b.rs_amt) / b.use_qnt : 0;
          return aAvg - bAvg;
        },
      },
    ];
  };

  // 獲取動態 saleColumns (使用快取)
  getSaleColumns = () => {
    return [
      {
        title: "訂單卡號",
        dataIndex: "ikey",
        key: "ikey",
        sorter: (a, b) =>
          `${a.ikey}-${a.ikey_seq_nos}`?.localeCompare(
            `${b.ikey}-${b.ikey_seq_nos}`
          ),
      },
      {
        title: "項次",
        dataIndex: "ikey_seq_nos",
        key: "ikey_seq_nos",
        sorter: (a, b) =>
          `${a.ikey}-${a.ikey_seq_nos}`?.localeCompare(
            `${b.ikey}-${b.ikey_seq_nos}`
          ),
      },
      {
        title: "訂單日期",
        dataIndex: "ins_dat",
        key: "ins_dat",
        sorter: (a, b) =>
          new Date(a.ins_dat).getTime() - new Date(b.ins_dat).getTime(),
      },
      {
        title: "取消日期",
        dataIndex: "cancel_dat",
        key: "cancel_dat",
        render: (value) => (value ? moment(value).format("YYYY-MM-DD") : "-"),
        sorter: (a, b) =>
          new Date(a.cancel_dat).getTime() - new Date(b.cancel_dat).getTime(),
      },
      {
        title: "訂房來源",
        dataIndex: "cust_nam",
        key: "cust_nam",
        sorter: (a, b) => a.cust_nam?.localeCompare(b.cust_nam),
      },
      {
        title: "團號",
        dataIndex: "atten_nam",
        key: "atten_nam",
        sorter: (a, b) => a.atten_nam?.localeCompare(b.atten_nam),
      },
      {
        title: "入住日期",
        dataIndex: "ci_dat",
        key: "ci_dat",
        render: (value) => moment(value).format("YYYY-MM-DD"),
        sorter: (a, b) =>
          new Date(a.ci_dat).getTime() - new Date(b.ci_dat).getTime(),
      },
      {
        title: "退房日期",
        dataIndex: "co_dat",
        key: "co_dat",
        render: (value) => moment(value).format("YYYY-MM-DD"),
        sorter: (a, b) =>
          new Date(a.co_dat).getTime() - new Date(b.co_dat).getTime(),
      },
      {
        title: "房價代碼",
        dataIndex: "rate_cod",
        key: "rate_cod",
        sorter: (a, b) => a.rate_cod?.localeCompare(b.rate_cod),
      },
      {
        title: "使用房種",
        dataIndex: "room_cod",
        key: "room_cod",
        sorter: (a, b) => a.room_cod?.localeCompare(b.room_cod),
        filters: this.state.saleRoomCodFilters,
        onFilter: (value, record) => record.room_cod === value,
        filterSearch: true,
      },
      {
        title: "計價房種",
        dataIndex: "use_cod",
        key: "use_cod",
        sorter: (a, b) => a.use_cod?.localeCompare(b.use_cod),
        filters: this.state.saleUseCodFilters,
        onFilter: (value, record) => record.use_cod === value,
        filterSearch: true,
      },
      {
        title: "房價 (含餐)",
        dataIndex: "rent_amt",
        key: "rent_amt",
        align: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.rent_amt - b.rent_amt,
      },
      {
        title: "間數",
        dataIndex: "order_qnt",
        key: "order_qnt",
        sorter: (a, b) => a.order_qnt - b.order_qnt,
      },
      {
        title: "入住天數",
        dataIndex: "days",
        key: "days",
        sorter: (a, b) => a.days - b.days,
      },
      {
        title: "房價小計",
        dataIndex: "total",
        key: "total",
        align: "right",
        fixed: "right",
        render: (value) => Number(value).toLocaleString(),
        sorter: (a, b) => a.total - b.total,
      },
    ];
  };

  render() {
    return (
      <Spin spinning={this.state.loading}>
        <h1>住房統計</h1>
        <Form>
          <Row gutter={5} justify="end">
            <Col xs={12} sm={12} lg={4}>
              <Form.Item label="週期">
                <Select
                  value={this.state.periodType}
                  onChange={this.handleSelectPeriodType}
                >
                  <Select.Option key="year">年</Select.Option>
                  <Select.Option key="month">月</Select.Option>
                  <Select.Option key="day">日</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={12} lg={4}>
              <Form.Item label="房型">
                <PmsRoomSelect
                  value={this.state.selectedRoomCod}
                  onChange={this.handleSelectRoomCode}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <ResponsiveECharts
          option={this.state.houseRateChartOption}
          onEvents={{
            click: this.handleClickChartEvent,
          }}
        />
        <Tabs
          activeKey={this.state.activeTabKey}
          onChange={(key) => this.setState({ activeTabKey: key })}
        >
          <Tabs.TabPane key="1" tab="總覽">
            <Table
              columns={this.getOverviewColumns()}
              dataSource={this.state.overviewData}
              scroll={{ x: "max-content" }}
              loading={this.state.loading}
              size="small"
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="2" tab="明細">
            <Row style={{ marginBottom: 10 }}>
              <Col span={18}>
                查詢日期: {this.state.detailDate ? this.state.detailDate : "無"}
              </Col>
            </Row>
            <Table
              columns={this.getColumns()}
              dataSource={this.state.details}
              scroll={{ x: "max-content" }}
              loading={this.state.detailLoading}
              size="small"
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="3" tab="銷售">
            <Row style={{ marginBottom: 10 }}>
              <Col xs={24} sm={24} lg={18}>
                <Form layout="inline">
                  <Form.Item label="查詢日期">
                    {this.state.detailDate ? this.state.detailDate : "無"}
                  </Form.Item>
                  <Form.Item label="當日間數小計">
                    {this.getSaleDetails().reduce(
                      (pv, cv, idx, arr) => pv + parseInt(cv.order_qnt),
                      0
                    )}
                  </Form.Item>
                  <Form.Item label="當日房價小計">
                    $
                    {this.getSaleDetails().reduce(
                      (pv, cv, idx, arr) => pv + cv.order_qnt * cv.rent_amt,
                      0
                    )}
                  </Form.Item>
                  <Form.Item label="間數小計">
                    {this.getSaleDetails().reduce(
                      (pv, cv, idx, arr) =>
                        pv + parseInt(cv.order_qnt * cv.days),
                      0
                    )}
                  </Form.Item>
                  <Form.Item label="房價總計">
                    $
                    {this.getSaleDetails().reduce(
                      (pv, cv, idx, arr) => pv + cv.total,
                      0
                    )}
                  </Form.Item>
                  <Form.Item label="房價平均">
                    $
                    {(
                      this.getSaleDetails().reduce(
                        (pv, cv, idx, arr) => pv + parseInt(cv.total),
                        0
                      ) /
                      this.getSaleDetails().reduce(
                        (pv, cv, idx, arr) =>
                          pv + parseInt(cv.order_qnt * cv.days),
                        0
                      )
                    ).toFixed(2)}
                  </Form.Item>
                  {/* <Form.Item label="明細總計">${this.getSaleDetails().reduce((pv, cv, idx, arr) => pv + parseInt(cv.rent_tot), 0)}</Form.Item> */}
                  {/* <Form.Item label="含餐平均">${(this.getSaleDetails().reduce((pv, cv, idx, arr) => pv + parseInt(cv.rent_tot), 0) / this.getSaleDetails().reduce((pv, cv, idx, arr) => pv + parseInt(cv.order_qnt * cv.days), 0)).toFixed(2)}</Form.Item> */}
                </Form>
              </Col>
              <Col xs={24} sm={24} lg={6} style={{ textAlign: "right" }}>
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  value={this.state.filterType}
                  onChange={this.handleSaleSelectFilter}
                  options={[
                    { label: "未取消", value: "not-canceled" },
                    { label: "已取消", value: "canceled" },
                    { label: "全部", value: "all" },
                  ]}
                />
              </Col>
            </Row>
            <Table
              columns={this.getSaleColumns()}
              dataSource={this.getSaleDetails()}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </Tabs.TabPane>
          <Tabs.TabPane key="4" tab="空房">
            <Row style={{ marginBottom: 10 }}>
              <Col span={18}>
                查詢日期: {this.state.detailDate ? this.state.detailDate : "無"}
              </Col>
            </Row>
            <Table
              columns={houseRemainColumns}
              dataSource={this.state.houseRemainDetails}
              scroll={{ x: "max-content" }}
              size="small"
            />
          </Tabs.TabPane>
        </Tabs>
      </Spin>
    );
  }
}

export default HouseRatePage;
